import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DEFAULT_ERROR_MESSAGE } from 'common/config';
import { ApiStatus, MessageType } from 'common/enums';
import { IErrorResponse } from 'common/models/api-service/error-response.model';
import { FieldError } from 'common/models';

import {
    SESSION_ARCHIVE_SUCCESS_MESSAGE,
    SESSION_DELETE_SUCCESS_MESSAGE
} from '../config/session.config';
import {
    NotificationPopupState,
    OrganiserDetail,
    SessionCode,
    SessionDetail
} from '../models/session.model';
import initialSessionState from './session.state';

const sessionSlice = createSlice({
    name: 'session',
    initialState: initialSessionState,
    reducers: {
        setSessionModalVisibility: (state, { payload }: PayloadAction<boolean>) => {
            state.isSessionModalVisible = payload;
        },
        setNotificationPopupStatus: (state, { payload }: PayloadAction<NotificationPopupState>) => {
            state.notificationPopupState = payload;
        },
        createSession: (state, { payload }: PayloadAction<any>) => {
            if (!state.sessionCreateStatus) state.sessionCreateStatus = {};
            state.sessionCreateStatus.status = ApiStatus.LOADING;
        },
        createSessionSuccess: (state) => {
            if (!state.sessionCreateStatus) state.sessionCreateStatus = {};
            state.sessionCreateStatus.status = ApiStatus.SUCCESS;
        },
        createSessionError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (!state.sessionCreateStatus) state.sessionCreateStatus = {};
            state.sessionCreateStatus.error = payload;
            state.sessionCreateStatus.status = ApiStatus.ERROR;
        },
        editSession: (state, { payload }: PayloadAction<any>) => {
            if (!state.sessionEditStatus) state.sessionEditStatus = {};
            state.sessionEditStatus.status = ApiStatus.LOADING;
        },
        editSessionSuccess: (state) => {
            if (!state.sessionEditStatus) state.sessionEditStatus = {};
            state.sessionEditStatus.status = ApiStatus.SUCCESS;
        },
        editSessionError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (!state.sessionEditStatus) state.sessionEditStatus = {};
            state.sessionEditStatus.error = payload;
            state.sessionEditStatus.status = ApiStatus.ERROR;
        },
        getSessionList: (state, { payload }: PayloadAction<any>) => {
            if (!state.sessionList) state.sessionList = {};
            state.sessionList.status = ApiStatus.LOADING;
        },
        getSessionListSuccess: (state, { payload }: PayloadAction<Array<SessionDetail>>) => {
            if (!state.sessionList) state.sessionList = {};
            state.sessionList.data = payload;
            state.sessionList.status = ApiStatus.SUCCESS;
        },
        getSessionListError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (!state.sessionList) state.sessionList = {};
            state.sessionList.error = payload;
            state.sessionList.status = ApiStatus.ERROR;
        },
        resetEditData: (state) => {
            state.editData = undefined;
        },
        initiateEditSession: (state, payload: any) => {
            const editData = {
                ...payload,
                organisers: payload.organisers.map((item: OrganiserDetail) => item.userId)
            };
            state.editData = editData;
            state.isSessionModalVisible = true;
        },
        deleteSession: (state, { payload }: PayloadAction<any>) => {
            if (!state.sessionDeleteStatus) state.sessionDeleteStatus = {};
            state.sessionDeleteStatus.status = ApiStatus.LOADING;
        },
        deleteSessionSuccess: (state) => {
            if (!state.sessionDeleteStatus) state.sessionDeleteStatus = {};
            state.sessionDeleteStatus.status = ApiStatus.SUCCESS;
            state.sessionDeleteStatus.message = SESSION_DELETE_SUCCESS_MESSAGE;
        },
        deleteSessionError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (!state.sessionDeleteStatus) state.sessionDeleteStatus = {};
            state.sessionDeleteStatus.error = payload;
            state.sessionDeleteStatus.status = ApiStatus.ERROR;
            state.sessionCreateStatus.message = DEFAULT_ERROR_MESSAGE;
        },
        archiveSession: (state, { payload }: PayloadAction<any>) => {
            if (!state.sessionArchiveStatus) state.sessionArchiveStatus = {};
            state.sessionArchiveStatus.status = ApiStatus.LOADING;
        },
        archiveSessionSuccess: (state) => {
            if (!state.sessionArchiveStatus) state.sessionArchiveStatus = {};
            state.sessionArchiveStatus.status = ApiStatus.SUCCESS;
            state.sessionArchiveStatus.message = SESSION_ARCHIVE_SUCCESS_MESSAGE;
        },
        archiveSessionError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (!state.sessionArchiveStatus) state.sessionArchiveStatus = {};
            state.sessionArchiveStatus.error = payload;
            state.sessionArchiveStatus.status = ApiStatus.ERROR;
            state.sessionArchiveStatus.message = DEFAULT_ERROR_MESSAGE;
        },
        getOrganiserList: (state) => {
            if (!state.sessionList) state.sessionList = {};
            state.sessionList.status = ApiStatus.LOADING;
        },
        getOrganiserListSuccess: (state, { payload }: PayloadAction<Array<SessionDetail>>) => {
            if (!state.sessionList) state.sessionList = {};
            state.sessionList.data = payload;
            state.sessionList.status = ApiStatus.SUCCESS;
        },
        getOrganiserListError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (!state.sessionList) state.sessionList = {};
            state.sessionList.error = payload;
            state.sessionList.status = ApiStatus.ERROR;
        },
        setSessionCode: (state, { payload }: PayloadAction<SessionCode>) => {
            state.sessionCode = payload;
        },
        resetSessionMessageStatus: (state) => {
            const messageStatus = {
                message: '',
                type: MessageType.Default
            };
            state.messageStatus = messageStatus;
        },
        setSessionFieldErrors: (state, { payload }: PayloadAction<Array<FieldError>>) => {
            state.fieldErrors = payload;
        }
    }
});

export default sessionSlice.reducer;
export const sessionActions = sessionSlice.actions;
